import React from 'react';
import { Spinner } from './Spinner';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  children: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  onClick,
  type = 'button',
  children,
  fullWidth = false,
  className,
}) => {
  const sizeMap = {
    sm: { height: 32, padding: '0 12px', fontSize: '14px' },
    md: { height: 40, padding: '0 16px', fontSize: '14px' },
    lg: { height: 48, padding: '0 20px', fontSize: '16px' },
  };

  const variantMap = {
    primary: {
      backgroundColor: 'var(--color-magenta)',
      color: 'var(--color-white)',
      border: 'none',
      '&:hover': { backgroundColor: 'var(--color-dark-magenta)' },
    },
    secondary: {
      backgroundColor: 'var(--color-white)',
      color: 'var(--color-magenta)',
      border: '1px solid var(--color-magenta)',
      '&:hover': { backgroundColor: 'var(--color-magenta-tint)' },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: 'var(--color-dark-grey)',
      border: 'none',
      '&:hover': { backgroundColor: 'var(--color-light-grey)' },
    },
    danger: {
      backgroundColor: '#dc3545',
      color: 'var(--color-white)',
      border: 'none',
      '&:hover': { backgroundColor: '#c82333' },
    },
  };

  const sizeStyle = sizeMap[size];
  const variantStyle = variantMap[variant];

  const baseStyle: React.CSSProperties = {
    display: fullWidth ? 'flex' : 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    minWidth: '44px',
    height: sizeStyle.height,
    padding: sizeStyle.padding,
    fontSize: sizeStyle.fontSize,
    fontFamily: 'var(--font-family)',
    fontWeight: 500,
    border: variantStyle.border || 'none',
    backgroundColor: variantStyle.backgroundColor,
    color: variantStyle.color,
    borderRadius: '4px',
    cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
    opacity: disabled || isLoading ? 0.6 : 1,
    transition: 'all 0.2s ease-in-out',
    width: fullWidth ? '100%' : 'auto',
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !isLoading && onClick) {
      onClick(e);
    }
  };

  return (
    <button
      type={type}
      style={baseStyle}
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={className}
    >
      {isLoading && <Spinner size="sm" label="Loading" />}
      {!isLoading && children}
    </button>
  );
};
